import { getToken, getTokenWithFormData } from '../../utils/auth';
import { apiSlice } from '../../reducers/index';
import {
  ISectionRequest,
  ISectionResponse,
  IDeleteSectionResponse,
  IFactSheetListResponse,
  IFactSheetRequest,
  IFactSheetResponse,
} from './plaintiffSheetsTypes';

export const plaintiffSheetsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    postSection: builder.mutation<ISectionResponse, ISectionRequest>({
      query: ({ data, factSheetId }) => {
        return {
          url: `/fact-sheets/${factSheetId}/sections`,
          method: 'POST',
          headers: getToken(),
          body: data,
        };
      },
      invalidatesTags: ['updateSection'],
    }),
    postFactSheet: builder.mutation<IFactSheetResponse, IFactSheetRequest>({
      query: ({ data }) => {
        return {
          url: `/fact-sheets`,
          method: 'POST',
          headers: getToken(),
          body: data,
        };
      },
      invalidatesTags: ['updateFactSheet'],
    }),

    updateSection: builder.mutation<ISectionResponse, ISectionRequest>({
      query: ({ data, factSheetId, sectionId }) => {
        return {
          url: `/fact-sheets/${factSheetId}/sections/${sectionId}`,
          method: 'PUT',
          headers: getToken(),
          body: data,
        };
      },
      invalidatesTags: ['updateSection'],
    }),
    getSectionById: builder.query<any, { districtId: string; sectionId: string }>({
      query: ({ districtId, sectionId }) => ({
        url: `/fact-sheet-responses/districts/${districtId}/sections/${sectionId}`,
        method: 'GET',
        headers: getToken(),
      }),
      providesTags: ['updateSection', 'updateField', 'updateTable'],
    }),

    getSectionsByDistrictId: builder.query<any, { districtId: string }>({
      query: ({ districtId }) => ({
        url: `/fact-sheet-responses/districts/${districtId}`,
        method: 'GET',
        headers: getToken(),
      }),
      providesTags: ['updateSection', 'updateField', 'updateTable'],
    }),
    getSections: builder.query({
      query: ({ factSheetId }) => ({
        url: `/fact-sheets/${factSheetId}/sections`,
        method: 'GET',
        headers: getToken(),
      }),
      providesTags: ['updateSection', 'updateField'],
    }),
    deleteSection: builder.mutation<
      IDeleteSectionResponse,
      { factSheetId: string; sectionId: string }
    >({
      query: ({ factSheetId, sectionId }) => ({
        url: `/fact-sheets/${factSheetId}/sections/${sectionId}`,
        method: 'DELETE',
        headers: getToken(),
      }),
      invalidatesTags: ['updateSection'],
    }),

    getAllFactSheets: builder.query<
      IFactSheetListResponse,
      { pageSize: number; pageNumber: number }
    >({
      query: ({ pageSize, pageNumber }) => ({
        url: `/fact-sheets?pageSize=${pageSize}&pageNumber=${pageNumber}`,
        method: 'GET',
        headers: getToken(),
      }),
      providesTags: ['updateFactSheet'],
    }),
    putSection: builder.mutation({
      query: ({ districtId, sectionId, payload }) => {
        return {
          url: `/fact-sheet-responses/districts/${districtId}/sections/${sectionId}`,
          method: 'PUT',
          headers: getToken(),
          body: payload,
        };
      },
      invalidatesTags: ['getSections', 'districtAttachments'],
    }),
    putCertification: builder.mutation({
      query: ({ districtId, sectionId, payload }) => {
        // Create FormData object if there are files to upload
        const formData = new FormData();
        // Append the file
        formData.append('signature', payload.file);
        // Append other fields
        formData.append('name', payload.name);
        formData.append('title', payload.title);
        return {
          url: `/certification/districts/${districtId}/sections/${sectionId}`,
          method: 'PUT',
          headers: getTokenWithFormData(), // This sets Content-Type: multipart/form-data
          body: formData,
        };
      },
      invalidatesTags: ['getCertification', 'districtAttachments'],
    }),
    getCertification: builder.query({
      query: ({ districtId, sectionId }) => {
        return {
          url: `/certification/districts/${districtId}/sections/${sectionId}`,
          method: 'GET',
          headers: getToken(),
        };
      },
      providesTags: ['getCertification'],
    }),
    getTableCellData: builder.query({
      query: ({ year, schoolLevel, tabName, districtId }) => {
        return {
          url: `/file-data/school-data?year=${year}&schoolLevel=${schoolLevel}&excelTabId=${tabName}&districtId=${districtId}`,
          method: 'GET',
          headers: getToken(),
        };
      },
    }),
    putFormSubmitted: builder.mutation({
      query: ({ districtId }) => {
        return {
          url: `fact-sheet-responses/submit/${districtId}`,
          method: 'PUT',
          headers: getToken(),
        };
      },
    }),
    deletUploadedFile: builder.mutation({
      query: ({ payload }) => {
        return {
          url: `/azure-container/delete`,
          method: 'DELETE',
          headers: getToken(),
          body: payload,
        };
      },
      invalidatesTags: ['districtAttachments'],
    }),
    updateDistrictStatus: builder.mutation<
      any,
      { districtId: string; status: string; comments: any[] }
    >({
      query: ({ districtId, status, comments }) => ({
        url: `fact-sheet-responses/admin/districts/${districtId}/status/${status}`,
        method: 'POST',
        headers: getToken(),
        body: comments,
      }),
      invalidatesTags: ['updateSection', 'updateFactSheet'],
    }),
    getDistrictComments: builder.query<any, { districtId: string }>({
      query: ({ districtId }) => ({
        url: `fact-sheet-responses/districts/${districtId}/comments`,
        method: 'GET',
        headers: getToken(),
      }),
      providesTags: ['districtComments'],
    }),

    updateDistrictComments: builder.mutation<any, { districtId: string; comments: any[] }>({
      query: ({ districtId, comments }) => ({
        url: `fact-sheet-responses/districts/${districtId}/comments`,
        method: 'PUT',
        headers: getToken(),
        body: comments,
      }),
      invalidatesTags: ['districtComments'],
    }),
    getSasUrl: builder.query<any, { blobName: string; containerName?: string }>({
      query: ({ blobName, containerName = 'documents' }) => ({
        url: `azure-container/sas-url`,
        method: 'GET',
        headers: getToken(),
        params: {
          blobName,
          containerName,
        },
      }),
    }),
    downloadSupplementalPdf: builder.mutation<Blob, { districtId: string }>({
      query: ({ districtId }) => ({
        url: `/supplemental-pdf/download/${districtId}`,
        method: 'GET',
        headers: getToken(),
        responseHandler: (response) => response.blob(),
      }),
    }),
    downloadFactsheetPdf: builder.mutation<Blob, { districtId: string }>({
      query: ({ districtId }) => ({
        url: `/factsheet/${districtId}/download-pdf`,
        method: 'GET',
        headers: getToken(),
        responseHandler: (response) => response.blob(),
      }),
    }),

    getDistrictAttachments: builder.query<any, { districtId: string }>({
      query: ({ districtId }) => ({
        url: `fact-sheet-responses/districts/${districtId}/attachments`,
        method: 'GET',
        headers: getToken(),
      }),
      providesTags: ({ districtId }) => [
        'districtAttachments',
        { type: 'districtAttachments', id: districtId },
      ],
    }),

    // Update the downloadAllAttachments mutation:

    // API definition
    // In your plaintiffSheetsEndpoint.ts file, replace the downloadAllAttachments mutation:

    // In your plaintiffSheetsEndpoint.ts file, replace your downloadAllAttachments mutation with this:
    // Updated downloadAllAttachments mutation with detailed debugging

    downloadAllAttachments: builder.mutation<{ blob: Blob }, { districtId: string }>({
      query: ({ districtId }) => ({
        url: `fact-sheet-responses/districts/${districtId}/attachments/download-all`,
        method: 'POST',
        headers: getToken(),
        responseHandler: async (response: Response) => {
          const blob = await response.blob();
          return { blob };
        },
      }),
    }),
  }),
});
export const {
  usePostSectionMutation,
  usePostFactSheetMutation,
  useGetSectionByIdQuery,
  useUpdateSectionMutation,
  useDeleteSectionMutation,
  useGetSectionsQuery,
  useGetAllFactSheetsQuery,
  usePutCertificationMutation,
  useGetCertificationQuery,
  usePutSectionMutation,
  useLazyGetTableCellDataQuery,
  usePutFormSubmittedMutation,
  useDeletUploadedFileMutation,
  useGetSectionsByDistrictIdQuery,
  useUpdateDistrictStatusMutation,
  useGetDistrictCommentsQuery,
  useUpdateDistrictCommentsMutation,
  useGetSasUrlQuery,
  useDownloadSupplementalPdfMutation,
  useDownloadFactsheetPdfMutation,
  useGetDistrictAttachmentsQuery,
  useDownloadAllAttachmentsMutation,
} = plaintiffSheetsApi;
